import { Link } from "react-router-dom";
import { BlogPageLayout } from "@/components/landing/BlogPageLayout";
import { SEO } from "@/components/SEO";
import { SchemaMarkup } from "@/components/seo/SchemaMarkup";
import { BreadcrumbNav } from "@/components/seo/BreadcrumbNav";
import { BlogFAQ } from "@/components/seo/BlogFAQ";
import { Calendar, Clock, ArrowRight, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";

const BASE = "https://sorvaine.com";

const faqItems = [
  { question: "How long does a typical RFP response take?", answer: "Mid-complexity RFPs (50–150 questions) take 25–45 hours of total team time. Simple RFPs run 10–20 hours; complex ones (150+ questions, multiple SMEs) regularly hit 60–120 hours." },
  { question: "What takes the longest in an RFP response?", answer: "Searching for past content and writing answers from scratch are the two biggest sinks, together usually 50–55% of total hours. SME chasing and final formatting are the next two." },
  { question: "Can AI speed up RFP responses?", answer: "Yes. Teams using AI-first tools typically cut total response time by 40–60% by automating requirement extraction, content retrieval, and first-draft generation — while keeping human review on the high-value sections." },
  { question: "How do I know if my team is fast or slow?", answer: "Benchmark your last five tenders on hours per response, hours per scored question, % content reused, and review cycles. If you're above 0.4 hours per scored question on mid-complexity bids, you have meaningful room to improve." },
  { question: "What's the fastest way to respond to an RFP?", answer: "Triage early, generate an AI first draft against the actual tender, assign section ownership on day one, and reserve human time for differentiation, pricing, and final review — not for hunting old answers." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 16, filter: "blur(4px)" },
  visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const } },
};

function Section({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <motion.section variants={fadeUp} initial="visible" whileInView="visible" viewport={{ once: true, amount: 0.2 }} className={className}>
      {children}
    </motion.section>
  );
}

export default function HowLongDoesRfpTake() {
  return (
    <BlogPageLayout>
      <SEO
        title="How Long Does an RFP Response Take? | Sorvaine"
        description="Real benchmarks for RFP response time by complexity, a section-by-section hour breakdown, and a self-assessment framework for your team."
        canonical={`${BASE}/resources/how-long-does-an-rfp-take`}
      />
      <SchemaMarkup type="article" title="How Long Does an RFP Response Take?" description="Real benchmarks for RFP response time by complexity, a section-by-section hour breakdown, and a self-assessment framework for your team." datePublished="2026-03-01" url={`${BASE}/resources/how-long-does-an-rfp-take`} />
      <SchemaMarkup type="faq" items={faqItems} />

      <article className="pt-28 pb-20 max-w-[900px] mx-auto px-6">
        <BreadcrumbNav items={[{ name: "Home", url: BASE }, { name: "Resources", url: `${BASE}/resources` }, { name: "How Long Does an RFP Take?", url: `${BASE}/resources/how-long-does-an-rfp-take` }]} />

        <motion.div initial="hidden" animate="visible" variants={fadeUp} className="mt-10 relative">
          <span className="absolute right-0 -top-2 text-[7rem] font-black text-gray-100 leading-none select-none pointer-events-none tracking-tight" aria-hidden="true">7 MIN</span>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-5">
              <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-primary/80">Guides</span>
              <span className="flex items-center gap-1 text-[11px] text-gray-400"><Calendar className="w-3 h-3" /> 1 Mar 2026</span>
              <span className="flex items-center gap-1 text-[11px] text-gray-400"><Clock className="w-3 h-3" /> 7 min read</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.08]">
              How Long Does an RFP Response Take?
            </h1>
            <p className="mt-5 text-lg text-gray-500 max-w-[640px] leading-relaxed">
              Real benchmarks by complexity, a section-by-section hour breakdown, and a self-assessment framework so you can finally answer "are we fast?".
            </p>
          </div>
        </motion.div>

        <div className="mt-14 space-y-14">
          <Section>
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900">The benchmark numbers</h2>
            <p className="mt-4 text-[15px] text-gray-500 leading-[1.75]">
              The honest answer is "it depends" — but it depends on a small number of variables you can actually measure. Across published industry surveys (APMP, Loopio's annual RFP report, Responsive's benchmarks), total team time on a tender clusters into three buckets:
            </p>
            <div className="mt-6 overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Complexity</th>
                    <th className="text-left px-4 py-3 font-semibold">Scored questions</th>
                    <th className="text-left px-4 py-3 font-semibold">Total team hours</th>
                    <th className="text-left px-4 py-3 font-semibold">Calendar time</th>
                    <th className="text-left px-4 py-3 font-semibold">SMEs involved</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-gray-600">
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-800">Simple</td>
                    <td className="px-4 py-3">&lt; 50</td>
                    <td className="px-4 py-3">10–20 hrs</td>
                    <td className="px-4 py-3">3–5 days</td>
                    <td className="px-4 py-3">1–2</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-800">Mid-complexity</td>
                    <td className="px-4 py-3">50–150</td>
                    <td className="px-4 py-3">25–45 hrs</td>
                    <td className="px-4 py-3">2–3 weeks</td>
                    <td className="px-4 py-3">3–5</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-800">Complex</td>
                    <td className="px-4 py-3">150–400</td>
                    <td className="px-4 py-3">60–120 hrs</td>
                    <td className="px-4 py-3">3–6 weeks</td>
                    <td className="px-4 py-3">5–10</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-800">Strategic / public sector</td>
                    <td className="px-4 py-3">400+</td>
                    <td className="px-4 py-3">150–400+ hrs</td>
                    <td className="px-4 py-3">6–12 weeks</td>
                    <td className="px-4 py-3">10+</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-gray-400">Hours include reading, planning, drafting, SME input, internal review, compliance check, and formatting. They exclude pricing strategy and BD/relationship work.</p>
          </Section>

          <Section>
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Where the hours actually go</h2>
            <p className="mt-4 text-[15px] text-gray-500 leading-[1.75]">
              For a typical 35-hour mid-complexity tender, here's the section-by-section breakdown teams consistently report:
            </p>
            <div className="mt-6 overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Phase</th>
                    <th className="text-left px-4 py-3 font-semibold">Hours</th>
                    <th className="text-left px-4 py-3 font-semibold">Share</th>
                    <th className="text-left px-4 py-3 font-semibold">Where AI helps most</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-gray-600">
                  <tr><td className="px-4 py-3 font-medium text-gray-800">Requirements analysis & compliance matrix</td><td className="px-4 py-3">4–5 hrs</td><td className="px-4 py-3">13%</td><td className="px-4 py-3">Auto-extract requirements, build matrix in minutes</td></tr>
                  <tr><td className="px-4 py-3 font-medium text-gray-800">Searching past content / Knowledge Vault</td><td className="px-4 py-3">7–9 hrs</td><td className="px-4 py-3">23%</td><td className="px-4 py-3">Semantic retrieval, instant answer matching</td></tr>
                  <tr><td className="px-4 py-3 font-medium text-gray-800">Writing & drafting</td><td className="px-4 py-3">9–11 hrs</td><td className="px-4 py-3">29%</td><td className="px-4 py-3">AI-generated first drafts per section</td></tr>
                  <tr><td className="px-4 py-3 font-medium text-gray-800">SME coordination</td><td className="px-4 py-3">3–4 hrs</td><td className="px-4 py-3">10%</td><td className="px-4 py-3">Targeted gaps only — most answers pre-drafted</td></tr>
                  <tr><td className="px-4 py-3 font-medium text-gray-800">Review, scoring & compliance check</td><td className="px-4 py-3">5–6 hrs</td><td className="px-4 py-3">16%</td><td className="px-4 py-3">Quality scoring, requirement coverage view</td></tr>
                  <tr><td className="px-4 py-3 font-medium text-gray-800">Formatting & submission</td><td className="px-4 py-3">2–3 hrs</td><td className="px-4 py-3">9%</td><td className="px-4 py-3">Branded Word export with TOC</td></tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-[15px] text-gray-500 leading-[1.75]">
              Two things stand out. First, less than a third of total time is actual writing — most of the budget is spent on retrieval, coordination, and checking. Second, the three biggest line items (search, drafting, review) are exactly where modern AI is strongest.
            </p>
          </Section>

          <Section>
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900">What drives the variance</h2>
            <p className="mt-4 text-[15px] text-gray-500 leading-[1.75]">
              Two teams responding to the "same size" tender can land 30 hours apart. The drivers are predictable:
            </p>
            <ul className="mt-4 space-y-2.5 text-[15px] text-gray-500 leading-[1.75] list-disc pl-5 marker:text-gray-300">
              <li><strong className="text-gray-700">Knowledge Vault maturity</strong> — a current, well-tagged answer library cuts retrieval time by 60–80%.</li>
              <li><strong className="text-gray-700">Number of SMEs in the chain</strong> — every additional SME adds roughly 2–4 hours of coordination overhead.</li>
              <li><strong className="text-gray-700">Number of review cycles</strong> — each full review pass adds 4–6 hours; teams that pre-score with AI typically need one fewer.</li>
              <li><strong className="text-gray-700">Tender format</strong> — portal-based questionnaires with strict character limits add 15–25% overhead vs free-form Word.</li>
              <li><strong className="text-gray-700">Pricing complexity</strong> — multi-line, optional, or rate-card pricing easily adds 5–10 hours on its own.</li>
            </ul>
          </Section>

          <Section>
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900">How AI changes the maths</h2>
            <p className="mt-4 text-[15px] text-gray-500 leading-[1.75]">
              AI-first tools like <Link to="/rfp-response-software" className="text-primary hover:underline font-medium">Sorvaine</Link> attack the three biggest line items directly. Compliance matrices are extracted automatically from the uploaded tender. Past content is retrieved semantically rather than searched manually. First drafts are generated for every section, so reviewers spend their time on quality, not on the blank page.
            </p>
            <div className="mt-5 p-5 rounded-xl border border-primary/20 bg-primary/[0.02]">
              <p className="text-sm font-semibold text-gray-700">The realistic impact</p>
              <p className="mt-1 text-[15px] text-gray-500 leading-[1.75]">A 35-hour mid-complexity tender drops to roughly <strong className="text-gray-700">14–20 hours</strong> with AI in the loop — a 40–60% reduction. Across 20 tenders a year, that's 300–400 hours returned to your team. The catch: AI doesn't remove human judgment, it removes the parts of the work nobody enjoyed anyway.</p>
            </div>
          </Section>

          <Section>
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Self-assessment: where does your team sit?</h2>
            <p className="mt-4 text-[15px] text-gray-500 leading-[1.75]">
              Take your last five tenders and answer these honestly. Score 1 point for each "yes":
            </p>
            <ul className="mt-4 space-y-2 text-[15px] text-gray-500 leading-[1.75]">
              {[
                "We log total hours per response, not just calendar days.",
                "We can state our average hours-per-scored-question for mid-complexity bids.",
                "Our Knowledge Vault was updated within the last 90 days.",
                "We finish first drafts in under 50% of total response time.",
                "We complete most tenders in two review cycles or fewer.",
                "Less than 25% of our time is spent searching for past content.",
                "Our SMEs spend less than 4 hours per tender on average.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
              <div className="p-4 rounded-xl border border-gray-200"><p className="font-semibold text-gray-800">0–2 yes</p><p className="text-gray-500 mt-1">Significant headroom. AI tooling typically pays back in the first 2–3 tenders.</p></div>
              <div className="p-4 rounded-xl border border-gray-200"><p className="font-semibold text-gray-800">3–5 yes</p><p className="text-gray-500 mt-1">Solid baseline. AI mostly removes drudge work and shortens review cycles.</p></div>
              <div className="p-4 rounded-xl border border-primary/30 bg-primary/[0.02]"><p className="font-semibold text-primary">6–7 yes</p><p className="text-gray-500 mt-1">Already excellent. AI compounds — focus on win-rate analytics next.</p></div>
            </div>
          </Section>

          <Section>
            <div className="flex flex-col md:flex-row gap-6 p-8 rounded-xl border border-primary/20 bg-primary/[0.02]">
              <div className="shrink-0 flex flex-col items-center justify-center p-4 rounded-lg bg-white border border-primary/10">
                <p className="text-3xl font-bold text-primary">40–60%</p>
                <p className="text-xs text-gray-500 mt-1">hours saved</p>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Cut your next tender in half</h3>
                <p className="mt-1.5 text-sm text-gray-500">Sorvaine drafts every section against the actual tender, builds the compliance matrix automatically, and grows your Knowledge Vault as you go.</p>
                <Link to="/subscribe" className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors active:scale-[0.97]">
                  Get Started <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Section>

          <Section>
            <BlogFAQ items={faqItems} />
          </Section>

          <Section>
            <h2 className="text-lg font-semibold text-gray-900">Keep reading on speed & process</h2>
            <div className="mt-4 space-y-2">
              <Link to="/resources/how-to-respond-to-an-rfp" className="block text-sm text-primary hover:underline">How to Respond to an RFP — step by step →</Link>
              <Link to="/resources/common-rfp-mistakes" className="block text-sm text-primary hover:underline">7 Common RFP Mistakes that cost hours →</Link>
              <Link to="/resources/proposal-content-library" className="block text-sm text-primary hover:underline">Build a Proposal Content Library that actually saves time →</Link>
            </div>
          </Section>
        </div>
      </article>
    </BlogPageLayout>
  );
}
